import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { Newspaper, Plus, FileText, CheckCircle2, Clock, Settings2, X, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}` } });

export default function PressInventoryList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inventories, setInventories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locLoaded, setLocLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [showSLModal, setShowSLModal] = useState(false);
  const [locked, setLocked] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/press-inventory`, authHeader());
      setInventories(res.data.inventories || []);
    } catch (e) {
      if (e?.response?.status === 403) setLocked(true);
      else toast.error('Inventuren konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const res = await axios.get(`${API}/api/user/locations`, authHeader());
      const locs = res.data || [];
      setLocations(locs);
      if (locs.length === 1) setLocationId(locs[0].id);  // genau einer → vorbelegen
    } catch (e) {
      toast.error('Standorte konnten nicht geladen werden');
    } finally {
      setLocLoaded(true);
    }
  };
  useEffect(() => { load(); loadLocations(); }, []);

  const locName = (id) => (locations.find(l => l.id === id) || {}).name || id || 'ohne Standort';

  const createInventory = async () => {
    if (!locationId) { toast.error('Bitte Standort wählen'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/press-inventory`,
        { location_id: locationId, inventory_date: date }, authHeader());
      toast.success('Presse-Inventur angelegt');
      navigate(`/inventur/presse/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally {
      setCreating(false);
    }
  };

  const drafts = inventories.filter(i => i.status === 'draft');
  const done = inventories.filter(i => i.status === 'finalized');

  if (locked) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="inventory-locked-notice">
          <div className="h-14 w-14 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-5">
            <Lock className="h-7 w-7 text-amber-600 dark:text-amber-300" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Inventur ist ab Business verfügbar</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Das Inventurmodul (Presse- und manuelle Inventur mit Pfand, Produkten, Vorlagen und PDF/CSV-Export)
            ist Teil der Pakete <strong>Business</strong> und <strong>Professional AI</strong>.
            Ihr aktuelles Paket enthält diese Funktion nicht.
          </p>
          <Button data-testid="inventory-upgrade-btn" onClick={() => navigate('/admin/subscription')}>
            Paket ansehen
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-6" data-testid="press-inventory-list">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
              <Newspaper className="h-6 w-6 text-indigo-600 dark:text-indigo-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Presse-Inventur</h1>
              <p className="text-sm text-muted-foreground">Presseexemplare zählen und nach VK + Steuersatz auswerten</p>
            </div>
          </div>
          {isAdmin && (
            <Button data-testid="sl-reopen-perm-btn" variant="outline" size="sm" className="gap-1.5" onClick={() => setShowSLModal(true)}>
              <Settings2 className="h-4 w-4" /> Wiederöffnen-Rechte
            </Button>
          )}
        </div>

        <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
          <h2 className="font-semibold text-foreground">Neue Presse-Inventur</h2>
          {!locLoaded ? (
            <p className="text-sm text-muted-foreground">Standorte werden geladen…</p>
          ) : locations.length === 0 ? (
            <p data-testid="no-location-msg" className="text-sm text-red-600">
              Ihnen ist kein Standort zugeordnet – eine Presse-Inventur kann nicht gestartet werden.
            </p>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Standort</label>
                  {locations.length === 1 ? (
                    <div data-testid="location-fixed" className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-muted text-foreground">
                      {locations[0].name}
                    </div>
                  ) : (
                    <select data-testid="location-select" value={locationId} onChange={e => setLocationId(e.target.value)}
                      className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground">
                      <option value="">Bitte wählen…</option>
                      {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  )}
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Inventurdatum</label>
                  <input data-testid="new-inv-date" type="date" value={date} onChange={e => setDate(e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground" />
                </div>
              </div>
              <Button data-testid="create-inventory-btn" onClick={createInventory} disabled={creating || !locationId} className="gap-2">
                <Plus className="h-4 w-4" /> {creating ? 'Wird angelegt…' : 'Inventur starten'}
              </Button>
            </>
          )}
        </div>

        {loading ? (
          <p className="text-muted-foreground">Lädt…</p>
        ) : (
          <>
            <Section title="Entwürfe" icon={<Clock className="h-4 w-4 text-amber-500" />} items={drafts} navigate={navigate} locName={locName} empty="Keine offenen Entwürfe" />
            <Section title="Abgeschlossen" icon={<CheckCircle2 className="h-4 w-4 text-emerald-500" />} items={done} navigate={navigate} locName={locName} empty="Noch keine abgeschlossenen Inventuren" />
          </>
        )}
      </div>
      {showSLModal && <PressSLPermissionModal onClose={() => setShowSLModal(false)} />}
    </DashboardLayout>
  );
}

function PressSLPermissionModal({ onClose }) {
  const [sls, setSls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/press-inventory/sl-permissions`, authHeader())
      .then(r => setSls(r.data || []))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (userId, current) => {
    setSaving(userId);
    try {
      await axios.patch(`${API}/api/press-inventory/sl-permission/${userId}`, { enabled: !current }, authHeader());
      setSls(prev => prev.map(sl => sl.id === userId ? { ...sl, press_reopen_permission: !current } : sl));
      toast.success('Berechtigung aktualisiert');
    } catch { toast.error('Fehler'); }
    finally { setSaving(null); }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="sl-perm-modal">
      <div className="bg-card w-full max-w-md rounded-2xl shadow-2xl my-auto max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-bold text-foreground">Wiederöffnen-Recht: Presse-Inventur</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Welche Stationsleitungen dürfen abgeschlossene Presse-Inventuren wieder öffnen?</p>
          </div>
          <button data-testid="sl-perm-close" onClick={onClose} className="p-2 rounded-lg hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-4 space-y-2">
          {loading ? <p className="text-sm text-muted-foreground">Lädt…</p>
            : sls.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="sl-perm-empty">Keine aktiven Stationsleitungen vorhanden.</p>
            : sls.map(sl => (
              <div key={sl.id} data-testid={`sl-perm-row-${sl.id}`} className="flex items-center justify-between gap-3 border border-border rounded-xl px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{sl.name || sl.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{sl.email}</p>
                </div>
                <button data-testid={`sl-perm-toggle-${sl.id}`} disabled={saving === sl.id}
                  onClick={() => toggle(sl.id, sl.press_reopen_permission)}
                  className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${sl.press_reopen_permission ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}>
                  {saving === sl.id ? '…' : sl.press_reopen_permission ? 'Erlaubt' : 'Gesperrt'}
                </button>
              </div>
            ))}
          <p className="text-[11px] text-muted-foreground pt-1">Standardmäßig gesperrt. Admins dürfen abgeschlossene Inventuren immer wieder öffnen. Änderungen wirken sofort (serverseitig live geprüft).</p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, items, navigate, locName, empty }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">{icon}{title}</div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : items.map(inv => (
        <button key={inv.id} data-testid={`inventory-row-${inv.id}`} onClick={() => navigate(`/inventur/presse/${inv.id}`)}
          className="w-full text-left bg-card border border-border rounded-xl px-4 py-3 flex items-center justify-between hover:bg-muted transition-colors">
          <div>
            <p className="font-medium text-foreground">{inv.inventory_date} · {locName ? locName(inv.location_id) : (inv.location_id || 'ohne Standort')}</p>
            <p className="text-xs text-muted-foreground">angelegt von {inv.created_by_name || '—'} · {String(inv.created_at).slice(0, 16).replace('T', ' ')}</p>
          </div>
          <FileText className="h-4 w-4 text-muted-foreground" />
        </button>
      ))}
    </div>
  );
}
